import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import ScriptFormatPicker from './ScriptFormatPicker';
import { getConcept, addArtifact, approveGate, writeBackProduction, produceConcept,
  cancelGeneration, setClassLength } from './api';
import ArtifactDetail from './ArtifactDetail';

const KINDS = [
  ['script', 'Script'],
  ['quiz', 'Quiz'],
  ['attachment', 'Handout / attachment'],
  ['exam', 'Exam'],
];

// Lead-up questions per kind. All optional - left blank, JPRO builds from the
// whole class context; answered, they steer the generation.
const ADD_QUESTIONS = {
  script: [
    { key: 'length', q: 'Approx length / depth?', ph: 'e.g. 40-min eLearning' },
    { key: 'tone', q: 'Tone or emphasis?', ph: 'e.g. practical, example-driven' },
  ],
  quiz: [
    { key: 'count', q: 'How many questions?', ph: 'e.g. 10' },
    { key: 'focus', q: 'Focus areas?', ph: 'e.g. common AMI qualification errors' },
  ],
  attachment: [
    { key: 'purpose', q: 'What is the handout for?', ph: 'e.g. a one-page desk reference' },
    { key: 'audience', q: 'Audience?', ph: 'e.g. leasing agents' },
    { key: 'keypoints', q: 'Key points to include?', ph: 'e.g. income-limit table, checklist' },
  ],
  exam: [
    { key: 'count', q: 'How many questions?', ph: 'e.g. 20' },
    { key: 'difficulty', q: 'Difficulty level?', ph: 'introductory / intermediate / advanced' },
    { key: 'format', q: 'Format notes?', ph: 'e.g. closed-book, multiple choice + 2 scenarios' },
    { key: 'emphasis', q: 'Topics to emphasize?', ph: 'e.g. recertification timing' },
  ],
};

// A class script is a minutes-long AI call, so production runs in a background
// job: the API answers 202 and the work shows up here as a "generating" status on
// the production (first build) or on the individual artifact (added later).
//
// generation_stalled means that status is never going to change (no worker, dead
// job). Those rows are treated as failures, so nothing polls forever and every
// stuck row has a way out.
const isWriting = (row) => row?.status === 'generating' && !row.generation_stalled;
const isStopped = (row) => row?.status === 'failed' || Boolean(row?.generation_stalled);

// "a few minutes" reads identically at ten seconds and at four hours. Showing the
// elapsed time is what turns a banner that might be lying into one that is checkable.
const elapsed = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const isGenerating = (concept) => {
  const p = concept?.production;
  if (!p) return false;
  return isWriting(p) || (p.artifacts || []).some(isWriting);
};

// initialArtifactId / initialAddOpen let the concept page hand off directly to the
// artifact the author clicked (or straight to the add dialog), so "Edit" from the
// class inventory lands on that artifact instead of the workspace's default.
export default function ProductionWorkspace({ conceptId, onBack, initialArtifactId = null,
  initialAddOpen = false }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['training-concept', conceptId],
    queryFn: () => getConcept(conceptId),
    // Poll only while something is being generated, then stop.
    refetchInterval: (q) => (isGenerating(q.state.data?.concept) ? 4000 : false),
  });
  const c = data?.concept;
  const prod = c?.production;
  const artifacts = prod?.artifacts || [];
  const generating = isGenerating(c);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['training-concept', conceptId] });

  const [selectedId, setSelectedId] = useState(initialArtifactId);
  const [addOpen, setAddOpen] = useState(initialAddOpen);
  const [addKind, setAddKind] = useState('attachment');
  const [addInstruction, setAddInstruction] = useState('');
  const [addAnswers, setAddAnswers] = useState({});
  // The format and narration rate the next build uses. Empty means "whatever the admin
  // default and the production already say", so opening the workspace changes nothing.
  const [buildOpts, setBuildOpts] = useState({});
  // Collapsing the artifact list gives the editor the full width, which matters most for
  // an A/V script: its table is three columns and the narration view is prose. Remembered
  // across visits, because someone who works collapsed wants to stay collapsed.
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem('jpro.training.artifactRail') === 'collapsed',
  );
  const toggleRail = () => {
    setRailCollapsed((was) => {
      const next = !was;
      localStorage.setItem('jpro.training.artifactRail', next ? 'collapsed' : 'expanded');
      return next;
    });
  };

  const selected = artifacts.find((a) => a.id === selectedId) || artifacts[0];

  const addMut = useMutation({
    mutationFn: () => {
      const qs = ADD_QUESTIONS[addKind] || [];
      const spec = qs.filter((q) => (addAnswers[q.key] || '').trim())
        .map((q) => `${q.q} ${addAnswers[q.key].trim()}`).join('\n');
      const instruction = [spec, addInstruction.trim()].filter(Boolean).join('\n') || undefined;
      return addArtifact(prod.id, { kind: addKind, instruction });
    },
    onSuccess: (res) => {
      toast.success('JPRO is writing it now - it will fill in here when it is done.');
      setAddOpen(false); setAddAnswers({}); setAddInstruction('');
      setSelectedId(res?.artifact_id || null);
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Could not add artifact'),
  });
  const approveGateMut = useMutation({
    mutationFn: (gate) => approveGate(conceptId, gate),
    onSuccess: () => { toast.success('Gate approved'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Not permitted for this gate'),
  });
  const writeBackMut = useMutation({
    mutationFn: () => writeBackProduction(prod.id),
    onSuccess: (res) => {
      toast.success('Write-back queued');
      // The API works out a correction when the request's program is not one we
      // recognize. It is only ever returned here, so dropping the response meant
      // nobody ever saw it and the class filed itself under a typo.
      const note = res?.program_note;
      if (note && !note.recognized) {
        const fix = note.suggestion
          ? `${note.is_typo ? 'Looks like a typo. ' : ''}Did you mean "${note.suggestion}"?`
          : 'It will be filed under that label as-is.';
        toast(`Program "${note.program}" is not on the known list. ${fix}`,
          { icon: '!', duration: 10000 });
      }
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Write-back disabled or failed'),
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelGeneration(prod.id),
    onSuccess: () => { toast.success('Stopped. You can start it again when you are ready.'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Could not stop this build'),
  });
  // The class length is on the concept, and approved_for_development locks the concept,
  // so this is its own audited endpoint rather than a concept PATCH.
  const lengthMut = useMutation({
    mutationFn: (minutes) => setClassLength(conceptId, minutes),
    onSuccess: () => {
      toast.success('Class length updated. Rebuild the script to write it to the new budget.');
      qc.invalidateQueries({ queryKey: ['training-concept', conceptId] });
    },
    onError: (e) => toast.error(e?.message || 'Could not change the class length'),
  });

  const produceMut = useMutation({
    mutationFn: (opts) => produceConcept(conceptId, opts || {}),
    onSuccess: () => {
      toast.success('JPRO is building the script and quiz. This takes a few minutes.');
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Production failed'),
  });

  if (isLoading) return <div className="p-6 text-sm text-gray-500">Loading production…</div>;
  // Don't fall through to the "not produced" screen on a load error - Produce is
  // destructive (it regenerates artifacts), so a transient failure must not offer it.
  if (isError) return (
    <div className="p-6">
      <button onClick={onBack} className="text-sm text-[#5ea9ff] mb-3">← Concept</button>
      <div className="text-sm text-red-500">Could not load production{error?.message ? `: ${error.message}` : ''}.</div>
    </div>
  );
  // Development is approved but nothing produced yet: build the first artifacts here.
  if (!prod) return (
    <div className="bg-[#0e1117] text-[#e6edf6] rounded-2xl min-h-[40vh] p-6">
      <button onClick={onBack} className="text-sm text-[#5ea9ff] mb-4">← Concept</button>
      <div className="text-sm font-semibold mb-2">{c?.title} · Production</div>
      <p className="text-xs text-[#8a95a8] mb-3 max-w-md">
        This class is approved for development. Produce its first artifacts (a script and a quiz) to
        start building - you can then edit them, steer them with JPRO, and add handouts or an exam.
      </p>
      <div className="max-w-md mb-3">
        <ScriptFormatPicker value={buildOpts} onChange={setBuildOpts}
          minutes={c?.recommended_length_minutes}
          delivery={c?.delivery_recommendation}
          onLengthChange={(m) => lengthMut.mutate(m)}
          lengthChanging={lengthMut.isPending}
          lengthLocked={c?.status !== 'approved_for_development'}
          lengthLockReason="The length can only be changed while the class is in development." />
      </div>
      <button onClick={() => produceMut.mutate(buildOpts)} disabled={produceMut.isPending}
        className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40">
        {produceMut.isPending ? 'Generating…' : 'Produce class artifacts'}
      </button>
    </div>
  );

  return (
    <div className="bg-[#0e1117] text-[#e6edf6] rounded-2xl min-h-[70vh]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1d2634]">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm text-[#5ea9ff]">← Concept</button>
          <span className="text-sm font-semibold">{c?.title} · Production</span>
        </div>
        <span className="text-[11px] text-[#8a95a8]">Status: {String(prod.status).replace(/_/g, ' ')}</span>
      </div>

      {/* Generation runs in the background, so the workspace says so rather than
          leaving the author looking at an empty artifact list. */}
      {generating && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[#0d2440] border-b border-[#1d2634] text-[11px] text-[#9ec5ff]">
          <span className="w-2 h-2 rounded-full bg-[#5ea9ff] animate-pulse" />
          <span>
            JPRO is writing. A class script can take a few minutes - this updates on its own, and you can leave the page.
            {/* The run reports its own age, so a build that has been going far too
                long is visible instead of hiding behind copy that never changes. */}
            {elapsed(prod.generation_elapsed_seconds) && (
              <span className="text-[#6f8cb0]"> Running for {elapsed(prod.generation_elapsed_seconds)}.</span>
            )}
          </span>
          {/* A way out that does not depend on waiting for the stall window.
              Only for a class build: this banner also shows while a single ADDED
              artifact writes (the production itself sitting at draft_ready), and
              cancelling the production then is a 422 - a button that cannot do what
              it says. Cancel targets the run that is actually in flight. */}
          {isWriting(prod) && (
            <button onClick={() => { if (window.confirm('Stop this build? Anything already written is kept, and you can start it again.')) cancelMut.mutate(); }}
              disabled={cancelMut.isPending}
              className="ml-auto px-2 py-1 rounded bg-[#1f2a38] border border-[#2b3a4d] text-[#9ec5ff] disabled:opacity-40 shrink-0">
              {cancelMut.isPending ? 'Stopping…' : 'Stop'}
            </button>
          )}
        </div>
      )}
      {isStopped(prod) && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#3a1d1d] border-b border-[#5c2b2b] text-[11px] text-[#ffb4b4]">
          <span>
            {prod.generation_error ||
              (prod.generation_stalled
                ? 'JPRO stopped partway through building this class and did not report back.'
                : 'JPRO could not finish building this class.')}
          </span>
          {/* retry, not force: this picks up where the build stopped and leaves
              any artifact that already has content (and any edit to it) alone. */}
          <button onClick={() => produceMut.mutate({ retry: true })} disabled={produceMut.isPending}
            className="px-2 py-1 rounded bg-[#1f6feb] text-white disabled:opacity-40">Try again</button>
        </div>
      )}

      <div className="px-4 py-3 border-b border-[#1d2634]">
        <ScriptFormatPicker value={buildOpts} onChange={setBuildOpts}
          minutes={c?.recommended_length_minutes}
          currentFormat={prod.script_format}
          hasArtifacts={(prod.artifacts || []).length > 0}
          delivery={c?.delivery_recommendation}
          onLengthChange={(m) => lengthMut.mutate(m)}
          lengthChanging={lengthMut.isPending}
          lengthLocked={c?.status !== 'approved_for_development'}
          lengthLockReason="The length can only be changed while the class is in development." />
        <button
          onClick={() => {
            const changing = buildOpts.script_format && buildOpts.script_format !== prod.script_format;
            const message = changing
              ? 'Rebuild this class as a ' + buildOpts.script_format.replace('_', ' ')
                + ' script? The current script is replaced.'
              : 'Rebuild this class from scratch? The current script and quiz are replaced.';
            if (!window.confirm(message)) return;
            produceMut.mutate({ ...buildOpts, force: true });
          }}
          disabled={produceMut.isPending || generating}
          className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">
          {produceMut.isPending ? 'Rebuilding…' : 'Rebuild the script'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-[#1d2634]">
        <span className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mr-1">Gates</span>
        {c.delivery_recommendation === 'live' ? (
          <button onClick={() => approveGateMut.mutate('scheduling')} disabled={approveGateMut.isPending}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">Approve for scheduling</button>
        ) : (
          <button onClick={() => approveGateMut.mutate('publication')} disabled={approveGateMut.isPending}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">Approve for publication</button>
        )}
        <button onClick={() => writeBackMut.mutate()} disabled={writeBackMut.isPending}
          className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] border border-[#2b3a4d] disabled:opacity-40">Write back to SharePoint</button>
        <span className="text-[10px] text-[#5f6b7e] ml-auto">Publication marks the class live in the LMS (deployment is manual).</span>
      </div>

      <div className="flex">
        {/* Left: artifact list + add. Collapses to a rail that still shows which artifact
            is selected and whether any of them is being written, so collapsing does not
            cost the author the state they were watching. */}
        {railCollapsed ? (
          <div className="w-10 shrink-0 border-r border-[#1d2634] py-3 flex flex-col items-center gap-2">
            <button onClick={toggleRail} aria-label="Show the artifact list"
              aria-expanded="false" title="Show the artifact list"
              className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff]">
              <PanelLeftOpen size={15} />
            </button>
            {artifacts.map((a) => (
              <button key={a.id} onClick={() => setSelectedId(a.id)}
                aria-label={`${a.kind}: ${a.title}`}
                title={`${a.kind} - ${isWriting(a) ? 'writing' : a.status}`}
                className={`w-7 h-7 rounded-lg text-[10px] uppercase border ${
                  selected?.id === a.id ? 'bg-[#12202f] border-[#2b3a4d] text-[#e6edf6]'
                    : 'border-transparent text-[#8a95a8] hover:bg-[#12202f]'}`}>
                {a.kind.slice(0, 2)}
              </button>
            ))}
            {/* Same action and the same guard as the expanded list: the class build
                rewrites the script and quiz as it runs, so additions are refused until it
                finishes, and the reason sits on the button rather than arriving as a 409. */}
            <button onClick={() => setAddOpen(true)} disabled={isWriting(prod)}
              aria-label="Add an artifact"
              title={isWriting(prod) ? 'Wait for the class build to finish' : 'Add an artifact'}
              className="p-1.5 rounded hover:bg-[#12202f] text-[#5ea9ff] disabled:opacity-40 disabled:cursor-not-allowed">
              <Plus size={15} />
            </button>
            {artifacts.some((a) => isWriting(a)) && (
              <span className="w-2 h-2 rounded-full bg-[#5ea9ff] animate-pulse"
                title="An artifact is being written" />
            )}
          </div>
        ) : (
        <div className="w-56 shrink-0 border-r border-[#1d2634] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">Artifacts</span>
            <button onClick={toggleRail} aria-label="Hide the artifact list"
              aria-expanded="true" title="Hide the artifact list, for more editing room"
              className="p-1 rounded hover:bg-[#12202f] text-[#8a95a8]">
              <PanelLeftClose size={14} />
            </button>
          </div>
          {artifacts.map((a) => (
            <button key={a.id} onClick={() => setSelectedId(a.id)}
              className={`block w-full text-left text-xs rounded-lg px-2 py-2 mb-1 border ${
                selected?.id === a.id ? 'bg-[#12202f] border-[#2b3a4d]' : 'border-transparent hover:bg-[#12202f]'}`}>
              <div className="font-medium">{a.kind}</div>
              <div className="text-[10px] text-[#8a95a8] truncate">{a.title}</div>
              <div className={`text-[10px] ${
                a.status === 'approved' ? 'text-green-400'
                  : isStopped(a) ? 'text-red-400'
                  : isWriting(a) ? 'text-[#5ea9ff]' : 'text-[#8a95a8]'}`}>
                {isWriting(a) ? 'writing…' : a.generation_stalled ? 'stopped' : a.status}
              </div>
            </button>
          ))}
          {/* The class build rewrites the script and quiz as it runs, so the API
              refuses additions until it finishes. Say so rather than let the
              author fill in the form and collect a 409. */}
          <button onClick={() => setAddOpen(true)} disabled={isWriting(prod)}
            title={isWriting(prod) ? 'Wait for the class build to finish' : undefined}
            className="mt-2 text-xs text-[#5ea9ff] disabled:opacity-40 disabled:cursor-not-allowed">
            + Add artifact
          </button>
        </div>
        )}

        {/* Right: selected artifact. The key remounts on switch; without it an
            open editor keeps the previous artifact's document and Save would
            write it into the newly selected one. */}
        <div className="flex-1 min-w-0 p-4">
          {!selected ? (
            <div className="text-sm text-gray-500">Select an artifact.</div>
          ) : (
            <ArtifactDetail key={selected.id} artifact={selected} onChanged={invalidate} />
          )}
        </div>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => { setAddOpen(false); setAddAnswers({}); setAddInstruction(''); }}>
          <div className="w-full max-w-md rounded-2xl bg-[#0e1117] border border-[#2b3a4d] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-[#e6edf6]">Add an artifact</div>
            <select value={addKind} onChange={(e) => { setAddKind(e.target.value); setAddAnswers({}); }}
              className="w-full text-xs bg-[#0f1620] border border-[#3a4a5f] rounded px-2 py-1.5 text-[#e6ecf5] [&>option]:bg-[#0f1620]">
              {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-[11px] text-[#7d8aa0]">
              A few lead-up questions (optional). Left blank, JPRO builds from the class context.
            </div>
            {(ADD_QUESTIONS[addKind] || []).map((q) => (
              <input key={q.key} value={addAnswers[q.key] || ''}
                onChange={(e) => setAddAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                placeholder={q.ph ? `${q.q} (${q.ph})` : q.q}
                className="w-full text-xs bg-transparent border border-[#2b3a4d] rounded px-2 py-1.5 text-[#c7d0e0]" />
            ))}
            <textarea value={addInstruction} onChange={(e) => setAddInstruction(e.target.value)}
              placeholder="Anything else JPRO should know…"
              className="w-full text-xs bg-transparent border border-[#2b3a4d] rounded px-2 py-1.5 min-h-[60px] text-[#c7d0e0]" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setAddOpen(false); setAddAnswers({}); setAddInstruction(''); }}
                className="text-xs px-3 py-1.5 text-[#8a95a8]">Cancel</button>
              <button onClick={() => addMut.mutate()} disabled={addMut.isPending}
                className="text-xs px-4 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40">
                {addMut.isPending ? 'Creating…' : 'Create with JPRO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
